import { useEffect, useState } from 'react';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import Button from '../../ui/Button';
import { Card, CardContent } from '../../ui/Card';
import irrigationNetworkService, {
  type IrrigationNode,
  type IrrigationLiveNetwork,
  type ControlMode,
  type SourceSubtype,
  type PumpCurvePoint,
  type SensorChannelOption,
} from '../../../services/irrigationNetwork';
import { errorMessage } from '../../../utils/apiResponse';
import DeviceLinkPanel from './DeviceLinkPanel';
import OrchardAreaPicker from './OrchardAreaPicker';
import IrrigationInstallPanel from './IrrigationInstallPanel';
import NodeEquipmentPicker from './NodeEquipmentPicker';
import type { RelaySlot } from '../../../utils/relayCatalogue';
import type { EspNowRouter, RelayZone } from '../../../services/espnowIrrigation';

interface Props {
  node: IrrigationNode;
  network: IrrigationLiveNetwork | null;
  editable: boolean;
  /** Every relay this farm's controllers can address — see `useRelayCatalogue`. */
  catalogue: RelaySlot[];
  routersByController: Record<string, EspNowRouter[]>;
  catalogueLoading: boolean;
  catalogueError: string | null;
  /** The relay whose area the map is currently drawing, if any. */
  drawingZoneFor?: string | null;
  onDrawZone?: (slot: RelaySlot) => void;
  onCancelDrawZone?: () => void;
  onZoneSaved?: (zone: RelayZone | null, routerMac: string, relayIndex: number) => void;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onDeleted: () => void | Promise<void>;
}

export default function NodeEditDrawer({
  node, network, editable, catalogue, routersByController,
  catalogueLoading, catalogueError,
  drawingZoneFor = null, onDrawZone, onCancelDrawZone, onZoneSaved,
  onClose, onSaved,
}: Props) {
  const [draft, setDraft] = useState<IrrigationNode>(node);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tankSensors, setTankSensors] = useState<SensorChannelOption[]>([]);

  useEffect(() => { setDraft(node); setError(null); }, [node]);

  // For tank sources, fetch the available water_level sensor channels on this farm
  useEffect(() => {
    if (node.node_type !== 'tank') return;
    irrigationNetworkService
      .listSensorChannels(node.farm_id, ['water_level'])
      .then(setTankSensors)
      .catch(() => setTankSensors([]));
  }, [node.node_type, node.farm_id]);

  const set = <K extends keyof IrrigationNode>(k: K, v: IrrigationNode[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await irrigationNetworkService.updateNode(node.id, {
        name: draft.name,
        notes: draft.notes,
        elevation_m: draft.elevation_m,
        elevation_source: draft.elevation_source,
        source_subtype: draft.source_subtype,
        rated_flow_lpm: draft.rated_flow_lpm,
        rated_pressure_kpa: draft.rated_pressure_kpa,
        pump_curve: draft.pump_curve,
        source_tank_sensor_channel_id: draft.source_tank_sensor_channel_id,
        control_mode: draft.control_mode,
        required_pressure_kpa: draft.required_pressure_kpa,
        min_operating_pressure_kpa: draft.min_operating_pressure_kpa,
        rated_valve_flow_lpm: draft.rated_valve_flow_lpm,
        served_area_ha: draft.served_area_ha,
        block_id: draft.block_id,
        cultivar_area_id: draft.cultivar_area_id,
        ...(node.node_type === 'source' ? { equipment_id: draft.equipment_id ?? null } : {}),
      });
      await onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const lookupElev = async () => {
    try {
      const r = await irrigationNetworkService.lookupElevation(draft.lat, draft.lng);
      if (r) {
        set('elevation_m', r.elevation_m);
        set('elevation_source', 'dem');
      } else {
        setError('No DEM data for that point');
      }
    } catch (e) { setError(errorMessage(e, 'Elevation lookup failed')); }
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm">
            {node.node_type.toUpperCase()} #{node.id}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2 text-sm">
          <Field label="Name">
            <input className="input" disabled={!editable}
              value={draft.name ?? ''}
              onChange={(e) => set('name', e.target.value)} />
          </Field>

          <Field label="Notes">
            <textarea className="input" rows={2} disabled={!editable}
              value={draft.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)} />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Lat"><input className="input" disabled value={draft.lat} /></Field>
            <Field label="Lng"><input className="input" disabled value={draft.lng} /></Field>
          </div>

          <div className="grid grid-cols-3 gap-2 items-end">
            <Field label="Elevation (m)">
              <input className="input" type="number" step="0.01" disabled={!editable}
                value={draft.elevation_m ?? ''}
                onChange={(e) => {
                  set('elevation_m', e.target.value === '' ? null : Number(e.target.value));
                  set('elevation_source', 'manual');
                }} />
            </Field>
            <Field label="Source">
              <div className="text-xs text-gray-600 py-1">{draft.elevation_source ?? '—'}</div>
            </Field>
            {editable && (
              <Button size="sm" variant="outline" onClick={lookupElev}>Auto</Button>
            )}
          </div>

          {/* Source-specific */}
          {node.node_type === 'source' && (
            <>
              <Field label="Source type">
                <select className="input" disabled={!editable}
                  value={draft.source_subtype ?? ''}
                  onChange={(e) => set('source_subtype', (e.target.value || null) as SourceSubtype | null)}>
                  <option value="">—</option>
                  <option value="pump">Pump</option>
                  <option value="tank">Tank</option>
                  <option value="mains">Mains</option>
                  <option value="bore">Bore</option>
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Rated flow (L/min)">
                  <input className="input" type="number" disabled={!editable}
                    value={draft.rated_flow_lpm ?? ''}
                    onChange={(e) => set('rated_flow_lpm', e.target.value === '' ? null : Number(e.target.value))} />
                </Field>
                <Field label="Rated pressure (kPa)">
                  <input className="input" type="number" disabled={!editable}
                    value={draft.rated_pressure_kpa ?? ''}
                    onChange={(e) => set('rated_pressure_kpa', e.target.value === '' ? null : Number(e.target.value))} />
                </Field>
              </div>

              <NodeEquipmentPicker
                value={draft.equipment_id ?? null}
                editable={editable}
                hasRelay={draft.relay_router_mac != null && draft.relay_index != null}
                onChange={(id) => set('equipment_id', id)}
              />

              {/* Pump-curve editor — only when subtype is pump */}
              {draft.source_subtype === 'pump' && (
                <PumpCurveEditor
                  curve={draft.pump_curve ?? []}
                  editable={editable}
                  onChange={(c) => set('pump_curve', c.length > 0 ? c : null)}
                />
              )}
            </>
          )}

          {/* Tank-specific: link a water-level sensor for variable-head modelling */}
          {node.node_type === 'tank' && (
            <Field label="Linked water-level sensor (variable head)">
              <select className="input" disabled={!editable}
                value={draft.source_tank_sensor_channel_id ?? ''}
                onChange={(e) => set('source_tank_sensor_channel_id',
                  e.target.value === '' ? null : Number(e.target.value))}>
                <option value="">— fixed head only —</option>
                {tankSensors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {(s.device_name || s.device_id)}
                    {s.channel_name ? ` · ${s.channel_name}` : ''} (ch{s.channel_index})
                  </option>
                ))}
              </select>
            </Field>
          )}

          {/* Valve-specific */}
          {node.node_type === 'valve' && (
            <>
              <Field label="Control mode">
                <select className="input" disabled={!editable}
                  value={draft.control_mode ?? 'manual'}
                  onChange={(e) => set('control_mode', e.target.value as ControlMode)}>
                  <option value="manual">Manual</option>
                  <option value="relay">Relay (auto-set when linked)</option>
                  <option value="planned">Planned</option>
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Rated flow (L/min)">
                  <input className="input" type="number" disabled={!editable}
                    value={draft.rated_valve_flow_lpm ?? ''}
                    onChange={(e) => set('rated_valve_flow_lpm', e.target.value === '' ? null : Number(e.target.value))} />
                </Field>
                <Field label="Required pressure (kPa)">
                  <input className="input" type="number" disabled={!editable}
                    value={draft.required_pressure_kpa ?? ''}
                    onChange={(e) => set('required_pressure_kpa', e.target.value === '' ? null : Number(e.target.value))} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Min operating pressure (kPa)">
                  <input className="input" type="number" disabled={!editable}
                    value={draft.min_operating_pressure_kpa ?? ''}
                    onChange={(e) => set('min_operating_pressure_kpa', e.target.value === '' ? null : Number(e.target.value))} />
                </Field>
                {/* Hectares of its planting this valve waters. Blank is a valid
                    answer — the derivation then shares whatever the other valves
                    leave over, which is the whole planting when it is the only
                    one. Only worth stating where the split is uneven. */}
                <Field label="Waters (ha)">
                  <input className="input" type="number" step="0.01" min="0"
                    disabled={!editable}
                    placeholder="share evenly"
                    value={draft.served_area_ha ?? ''}
                    onChange={(e) => set('served_area_ha', e.target.value === '' ? null : Number(e.target.value))} />
                </Field>
              </div>
            </>
          )}

          <OrchardAreaPicker
            node={draft}
            editable={editable}
            onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
          />

          {/* What is installed in the planting the picker above names, and the
              flow it implies for this valve. Below the picker on purpose: with
              no cultivar area attributed there is nothing to describe, and the
              panel says so rather than appearing empty. */}
          <IrrigationInstallPanel
            node={draft}
            editable={editable}
            onNodeChanged={onSaved}
          />

          <DeviceLinkPanel
            node={draft}
            network={network}
            catalogue={catalogue}
            routersByController={routersByController}
            catalogueLoading={catalogueLoading}
            catalogueError={catalogueError}
            editable={editable}
            onLinked={async (updated) => { setDraft(updated); await onSaved(); }}
            drawingZoneFor={drawingZoneFor}
            onDrawZone={(slot) => onDrawZone?.(slot)}
            onCancelDrawZone={() => onCancelDrawZone?.()}
            onZoneSaved={(zone, mac, index) => onZoneSaved?.(zone, mac, index)}
          />

          {error && <div className="text-red-600 text-xs">{error}</div>}

          {editable && (
            <Button size="sm" onClick={save} disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
      </CardContent>
      <style>{`.input{width:100%;border:1px solid #d1d5db;border-radius:6px;padding:4px 8px;font-size:0.875rem;background:#fff}`}</style>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-600 mb-0.5">{label}</div>
      {children}
    </div>
  );
}

function PumpCurveEditor({
  curve, editable, onChange,
}: {
  curve: PumpCurvePoint[];
  editable: boolean;
  onChange: (c: PumpCurvePoint[]) => void;
}) {
  const setPoint = (i: number, key: 'flow_lpm' | 'head_m', v: number) => {
    const next = curve.map((p, idx) => idx === i ? { ...p, [key]: v } : p);
    onChange(next);
  };
  const addPoint = () => {
    const last = curve[curve.length - 1];
    onChange([...curve, {
      flow_lpm: last ? last.flow_lpm + 50 : 0,
      head_m:   last ? Math.max(0, last.head_m - 5) : 50,
    }]);
  };
  const removePoint = (i: number) => onChange(curve.filter((_, idx) => idx !== i));

  return (
    <div className="border rounded p-2 bg-gray-50">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-semibold">Pump curve</div>
        {editable && (
          <button onClick={addPoint} className="text-xs text-blue-600 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add point
          </button>
        )}
      </div>
      {curve.length === 0 ? (
        <div className="text-xs text-gray-500">No curve points — pump will use fixed-head fallback (rated pressure).</div>
      ) : (
        <div className="space-y-1">
          <div className="grid grid-cols-[1fr_1fr_auto] gap-1 text-[10px] text-gray-500 px-1">
            <div>Flow (L/min)</div><div>Head (m)</div><div /></div>
          {curve.map((p, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-1">
              <input className="input" type="number" step="1" disabled={!editable}
                value={p.flow_lpm}
                onChange={(e) => setPoint(i, 'flow_lpm', Number(e.target.value))} />
              <input className="input" type="number" step="0.1" disabled={!editable}
                value={p.head_m}
                onChange={(e) => setPoint(i, 'head_m', Number(e.target.value))} />
              {editable ? (
                <button onClick={() => removePoint(i)} className="text-red-500 px-1"><Trash2 className="w-3 h-3" /></button>
              ) : <span />}
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-gray-500 mt-1">
        Typical: 3 points — shutoff (flow 0, max head), duty point, run-out (max flow, low head). Sorted ascending by flow at solve time. Copy the chart as drawn: where it rises just off shutoff, points whose head does not fall are ignored and the solve says so.
      </div>
    </div>
  );
}
